import { useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface FaqItem {
  q: string;
  a: string;
  category: FaqCategory;
}

type FaqCategory =
  | 'plataforma'
  | 'pagamento'
  | 'contratacao'
  | 'seguranca'
  | 'ia'
  | 'advogados'
  | 'disputas';

const CATEGORY_LABELS: Record<FaqCategory, string> = {
  plataforma: 'Plataforma e funcionamento',
  pagamento: 'Pagamento e escrow',
  contratacao: 'Contratação e entrega',
  seguranca: 'Segurança e privacidade',
  ia: 'Inteligência artificial',
  advogados: 'Para advogados',
  disputas: 'Disputas e mediação',
};

const FAQ_DATA: FaqItem[] = [
  // Plataforma
  {
    category: 'plataforma',
    q: 'O que é o Parecer.ai?',
    a: 'É uma plataforma jurídica que conecta clientes (PF ou PJ) a advogados parceiros. Você pode subir um contrato pra revisão por IA + advogado, contratar consultoria, parecer escrito, divórcio, chat consultivo e mais. Pagamento via escrow (Pagar.me).',
  },
  {
    category: 'plataforma',
    q: 'Tem mensalidade?',
    a: 'Não. Para clientes e advogados, é zero. A plataforma só cobra 15% por cima do preço de cada tarefa fechada.',
  },
  {
    category: 'plataforma',
    q: 'Quais tipos de serviço posso contratar?',
    a: 'Hoje suportamos 12 tipos: revisão de contrato (simples e complexa), redação do zero, parecer jurídico escrito, chat consultivo, call/reunião, reunião de diagnóstico, orientação preventiva, consultoria estratégica, análise de caso, direito de família (divórcio, guarda, pensão) e negociação extrajudicial. Documento não é obrigatório pra todos.',
  },
  {
    category: 'plataforma',
    q: 'Preciso ter um documento pra abrir uma demanda?',
    a: 'Não. Para divórcio, guarda, consultoria, orientação preventiva e outros serviços, basta descrever sua situação. A IA classifica e indica advogados. Se você tem um contrato pra revisão, pode subir junto.',
  },

  // Pagamento
  {
    category: 'pagamento',
    q: 'Como funciona o escrow?',
    a: 'Quando você contrata, o pagamento é processado via Pagar.me (PIX, cartão ou boleto) e fica retido. Após o advogado entregar e você aprovar, o valor é liberado com split 85/15 (85% advogado / 15% plataforma). Se você não aprova em 7 dias úteis sem motivo, o sistema libera automaticamente. Em caso de disputa, a equipe Parecer.ai medeia.',
  },
  {
    category: 'pagamento',
    q: 'Quem define os preços?',
    a: 'Cada advogado individualmente, por tipo de tarefa. A plataforma adiciona 15% em cima e não tem ingerência sobre valores.',
  },
  {
    category: 'pagamento',
    q: 'Posso cancelar antes do advogado começar?',
    a: 'Sim. Cancelamento antes do início da revisão tem estorno integral. Após o início, depende do trabalho já executado — entra em fluxo de mediação se houver divergência.',
  },
  {
    category: 'pagamento',
    q: 'Posso emitir NF?',
    a: 'Sim. O advogado emite a NF dele direto pra você (ou pra sua empresa, se for PJ) — a plataforma não centraliza essa emissão. Por isso pedimos CPF/CNPJ no cadastro.',
  },

  // Contratação
  {
    category: 'contratacao',
    q: 'Como escolho um advogado?',
    a: 'Você pode (a) descrever sua necessidade em linguagem natural — nossa IA classifica e sugere advogados especializados, ou (b) navegar o marketplace de advogados e filtrar por área, preço, OAB verificada, senioridade.',
  },
  {
    category: 'contratacao',
    q: 'Posso baixar meu contrato a qualquer momento?',
    a: 'Sempre. Original e todas as versões revisadas ficam acessíveis no seu dashboard, com link de download direto.',
  },
  {
    category: 'contratacao',
    q: 'Quanto tempo leva uma entrega?',
    a: 'Depende do serviço e do advogado. Cada advogado define o prazo padrão na tabela de preços dele (horas pra entrega, minutos pra sessão, dias de acesso pra chat). Você vê o prazo antes de contratar.',
  },
  {
    category: 'contratacao',
    q: 'Quem fica com o original do contrato após a revisão?',
    a: 'Você. O advogado entrega a versão revisada (com comentários ou tracked changes), mas o documento é seu. Backup automático na plataforma com retenção de 2 anos.',
  },

  // Segurança
  {
    category: 'seguranca',
    q: 'Meus documentos são seguros?',
    a: 'Sim. Contratos são criptografados em repouso (S3 + KMS) e em trânsito (TLS 1.2+). Apenas você e o advogado contratado têm acesso. Nenhum advogado vê seu documento até você escolhê-lo. Backup com retenção de 2 anos. Conformidade LGPD.',
  },
  {
    category: 'seguranca',
    q: 'A IA tem acesso ao meu contrato?',
    a: 'A análise IA roda em ambiente segregado da Anthropic via AWS Bedrock. Seu documento NÃO é usado para treinar modelos. Não há cópia persistente fora da sua conta na plataforma.',
  },
  {
    category: 'seguranca',
    q: 'Outros advogados vão ver meu caso?',
    a: 'Não. Antes da contratação, advogados só veem a descrição (que você escreveu) — nunca o documento. Após contratação, apenas o advogado escolhido tem acesso. Você pode rescindir o acesso a qualquer momento.',
  },
  {
    category: 'seguranca',
    q: 'O advogado tem dever de sigilo?',
    a: 'Sim. Sigilo profissional do advogado é previsto no Estatuto da OAB (Art. 7º, II) e ratificado no Termo de Parceria aceito no cadastro. Quebra de sigilo é causa de suspensão permanente da plataforma + sanção OAB.',
  },

  // IA
  {
    category: 'ia',
    q: 'A análise da IA substitui um advogado?',
    a: 'Não. A análise da IA é puramente informativa — identifica cláusulas potencialmente sensíveis, riscos comuns e oferece resumos pra orientar sua conversa com um advogado. NÃO substitui um parecer jurídico nem cria relação cliente-advogado. Decisões legais devem ser tomadas com base no parecer formal de um advogado contratado.',
  },
  {
    category: 'ia',
    q: 'Qual modelo de IA vocês usam?',
    a: 'Claude da Anthropic, rodando via AWS Bedrock em ambiente brasileiro. Modelos atualizados conforme a Anthropic libera versões mais novas.',
  },
  {
    category: 'ia',
    q: 'A IA pode errar?',
    a: 'Sim, pode. Como qualquer ferramenta, a IA tem limitações — pode interpretar cláusulas atípicas de forma incorreta, perder nuances específicas do caso ou omitir riscos. Sempre valide com um advogado antes de tomar decisões.',
  },

  // Advogados
  {
    category: 'advogados',
    q: 'Como me cadastro como advogado?',
    a: 'Em /cadastro/advogado. Você preenche dados pessoais, OAB, endereço profissional, especialidades, bio, aceita os termos. Após confirmar email, sua OAB passa por verificação manual pela equipe Parecer.ai antes do perfil ficar público.',
  },
  {
    category: 'advogados',
    q: 'Quanto tempo demora a verificação da OAB?',
    a: 'Atualmente até 5 dias úteis (verificação manual via consulta CNA). Você é notificado por email + banner no dashboard quando aprovada. Automação via OCR + consulta automática CNA está no roadmap.',
  },
  {
    category: 'advogados',
    q: 'Posso recusar uma tarefa que recebi?',
    a: 'Sim. Tarefas no marketplace aberto são opcionais — você aceita ou ignora. Tarefas direcionadas (cliente escolheu você especificamente) podem ser recusadas com um motivo curto. Recusas frequentes podem afetar prioridade no ranking IA.',
  },
  {
    category: 'advogados',
    q: 'Como recebo o pagamento?',
    a: 'Via Pagar.me. Você cadastra sua conta bancária no Pagar.me (recipient), recebe 85% direto após o cliente aprovar a entrega. Repasse em até 30 dias por padrão; cadastros com KYC completo podem optar por D+2.',
  },

  // Disputas
  {
    category: 'disputas',
    q: 'O que acontece se eu não gostar do serviço entregue?',
    a: 'Você pode abrir uma disputa direto pela tarefa. A equipe Parecer.ai analisa a conversa, os arquivos entregues e o SLA combinado. Decisão em até 5 dias úteis. Veja o fluxo completo em /termos-mediacao.',
  },
  {
    category: 'disputas',
    q: 'O advogado pode abrir disputa contra mim?',
    a: 'Sim — se você sumir após receber a entrega (aprovação automática em 7 dias), exigir escopo fora do combinado ou tiver comportamento abusivo. Veja /termos-mediacao.',
  },
  {
    category: 'disputas',
    q: 'A decisão da mediação é final?',
    a: 'Para fins de liberação do escrow, sim. Cada lado pode pedir uma reavaliação (uma vez). Direitos legais continuam preservados independentemente — você pode recorrer aos órgãos competentes.',
  },
];

const CATEGORIES: FaqCategory[] = [
  'plataforma',
  'pagamento',
  'contratacao',
  'seguranca',
  'ia',
  'advogados',
  'disputas',
];

export function Faq() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<FaqCategory | 'all'>('all');
  const [open, setOpen] = useState<Set<string>>(new Set());

  const normalized = useMemo(
    () =>
      search
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, ''),
    [search],
  );

  const filtered = useMemo(() => {
    let arr = FAQ_DATA;
    if (activeCategory !== 'all') arr = arr.filter((f) => f.category === activeCategory);
    if (normalized) {
      arr = arr.filter((f) => {
        const t = `${f.q} ${f.a}`
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '');
        return t.includes(normalized);
      });
    }
    return arr;
  }, [activeCategory, normalized]);

  function toggle(q: string): void {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(q)) next.delete(q);
      else next.add(q);
      return next;
    });
  }

  return (
    <MarketingLayout>
      <div className="container-px mx-auto max-w-4xl py-16">
        <Badge variant="accent" className="mb-2">FAQ</Badge>
        <h1 className="font-serif text-display-lg">Perguntas frequentes</h1>
        <p className="text-muted-foreground mt-2">
          Tudo que você precisa saber pra usar o Parecer.ai, organizado por tema.
        </p>

        {/* Search */}
        <div className="relative mt-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Buscar (ex: escrow, LGPD, advogado, IA...)"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Categories */}
        <div className="mt-6 flex flex-wrap gap-2">
          <CategoryChip
            active={activeCategory === 'all'}
            onClick={() => setActiveCategory('all')}
            label="Tudo"
            count={FAQ_DATA.length}
          />
          {CATEGORIES.map((c) => {
            const count = FAQ_DATA.filter((f) => f.category === c).length;
            return (
              <CategoryChip
                key={c}
                active={activeCategory === c}
                onClick={() => setActiveCategory(c)}
                label={CATEGORY_LABELS[c]}
                count={count}
              />
            );
          })}
        </div>

        {/* List */}
        <div className="mt-8 space-y-2">
          {filtered.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                Não encontramos respostas pra "{search}". Tente outras palavras ou{' '}
                <a href="mailto:contato@parecer.ai" className="text-accent hover:underline">
                  fale com a gente
                </a>
                .
              </CardContent>
            </Card>
          )}
          {filtered.map((f) => {
            const isOpen = open.has(f.q);
            return (
              <div
                key={f.q}
                className="rounded-md border border-border/60 bg-card transition-colors"
              >
                <button
                  type="button"
                  onClick={() => toggle(f.q)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-muted/30"
                  aria-expanded={isOpen}
                >
                  <span className="flex items-start gap-3">
                    <Badge variant="muted" className="text-[10px] mt-0.5 flex-none">
                      {CATEGORY_LABELS[f.category].split(' ')[0]}
                    </Badge>
                    <span className="font-medium">{f.q}</span>
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground transition-transform flex-none',
                      isOpen && 'rotate-180',
                    )}
                  />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 text-sm text-muted-foreground text-pretty">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-md border border-accent/30 bg-accent/5 p-5">
          <p className="font-medium">Não encontrou o que procurava?</p>
          <p className="text-sm text-muted-foreground mt-1">
            Mande um email pra{' '}
            <a href="mailto:contato@parecer.ai" className="text-accent hover:underline">
              contato@parecer.ai
            </a>{' '}
            — respondemos em até 1 dia útil. Nossa equipe é pequena e atende cada mensagem
            individualmente.
          </p>
        </div>
      </div>
    </MarketingLayout>
  );
}

function CategoryChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors',
        active
          ? 'border-accent bg-accent/15 text-foreground font-medium'
          : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground',
      )}
    >
      {label}
      <span className={cn('text-[10px]', active ? 'text-accent' : 'text-muted-foreground/60')}>
        {count}
      </span>
    </button>
  );
}
